import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AuiIf,
  ComposerPrimitive,
  useAui,
  useAuiState,
} from "@assistant-ui/react";
import { ArrowUpIcon, Plus, Square } from "lucide-react";
import { ChipIcon } from "./ChipIcon";
import { ContextChipBadge } from "./ContextChipBadge";
import {
  attachmentToChip,
  chipToAttachment,
  type ContextChip,
} from "./contextChips";

const MIN_TEXTAREA_HEIGHT = 48;
const MAX_TEXTAREA_HEIGHT = 320;

/** Prompt composer with pinned context chips and a page-aware "+" menu. */
export function CopilotComposer({
  pageSuggestions,
}: {
  pageSuggestions: ContextChip[];
}) {
  const [showContextMenu, setShowContextMenu] = useState(false);
  const [isSettled, setIsSettled] = useState(false); // Prevents initial layout glitch
  const menuRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const aui = useAui();

  // Chips pinned to the message currently being composed. Sourced directly
  // from the composer's own attachment list (assistant-ui) rather than local
  // state, so they are guaranteed to travel with the message on send and
  // clear from the composer automatically afterwards.
  const composerAttachments = useAuiState((s) => s.composer.attachments);
  const chips = useMemo(
    () => composerAttachments.map((a) => attachmentToChip(a)),
    [composerAttachments],
  );

  const composerText = useAuiState((s) => s.composer.text);

  const resizeTextarea = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;

    // Keep the composer at a single-line baseline when empty.
    if (el.value.length === 0) {
      el.style.height = `${MIN_TEXTAREA_HEIGHT}px`;
      el.style.overflowY = "hidden";
      return;
    }

    el.style.height = "auto";
    const nextHeight = Math.min(
      Math.max(el.scrollHeight, MIN_TEXTAREA_HEIGHT),
      MAX_TEXTAREA_HEIGHT,
    );
    el.style.height = `${nextHeight}px`;
    el.style.overflowY =
      el.scrollHeight > MAX_TEXTAREA_HEIGHT ? "auto" : "hidden";
  }, []);

  // Defer focus and height calculation until flexbox settles its true width
  useEffect(() => {
    setIsSettled(true);
    const timer = setTimeout(() => {
      textareaRef.current?.focus();
    }, 30);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!isSettled) return;
    const raf = requestAnimationFrame(() => {
      resizeTextarea();
    });
    return () => cancelAnimationFrame(raf);
  }, [composerText, isSettled, resizeTextarea]);

  const addChip = useCallback(
    (chip: ContextChip) => {
      void aui.composer().addAttachment(chipToAttachment(chip));
      setShowContextMenu(false);
    },
    [aui],
  );

  const removeChip = useCallback(
    (id: string) => {
      void aui.composer().attachment({ id }).remove();
    },
    [aui],
  );

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setShowContextMenu(false);
      }
    };
    if (showContextMenu) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showContextMenu]);

  const availableSuggestions = pageSuggestions.filter(
    (s) => !chips.find((c) => c.id === s.id),
  );

  return (
    <div className="flex flex-col gap-2 w-full">
      {chips.length > 0 && (
        <div className="flex flex-wrap gap-1.5 px-1">
          {chips.map((chip) => (
            <ContextChipBadge key={chip.id} chip={chip} onRemove={removeChip} />
          ))}
        </div>
      )}

      <ComposerPrimitive.Root className="flex w-full flex-col rounded-xl border bg-muted border-neutral-700/60 bg-neutral-800 transition-colors focus-within:border-sky-400/50">
        <ComposerPrimitive.Input
          ref={textareaRef}
          placeholder="Ask anything..."
          className="min-h-7 w-full resize-none bg-transparent px-5 pt-4 pb-3 text-sm focus:outline-none text-neutral-100 placeholder-neutral-500 outline-none"
          rows={1}
        />

        <div className="flex items-center justify-between px-3 pb-3">
          <div className="relative shrink-0" ref={menuRef}>
            <button
              type="button"
              onClick={() => setShowContextMenu((v) => !v)}
              title="Add context"
              className={`flex h-7 w-7 items-center justify-center rounded-md transition-colors ${
                showContextMenu
                  ? "border-sky-500/40 bg-sky-500/15 text-sky-300"
                  : "border-neutral-700 text-neutral-500 hover:border-neutral-600 hover:bg-neutral-800 hover:text-neutral-300"
              }`}
            >
              <Plus className="h-5 w-5" />
            </button>

            {showContextMenu && (
              <div className="absolute bottom-full left-0 z-50 mb-2 min-w-[220px] rounded-xl border border-neutral-700/70 bg-neutral-900 p-1 shadow-xl shadow-black/40">
                <p className="px-2 pb-1 pt-1.5 text-[10px] font-semibold uppercase tracking-wider text-neutral-600">
                  Add context
                </p>
                {availableSuggestions.length > 0 ? (
                  availableSuggestions.map((s) => (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => addChip(s)}
                      className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-xs text-neutral-300 hover:bg-neutral-800 hover:text-neutral-100"
                    >
                      <ChipIcon icon={s.icon} />
                      <span className="text-neutral-500">{s.label}:</span>
                      <span className="truncate">{s.displayValue}</span>
                    </button>
                  ))
                ) : (
                  <p className="px-2 py-2.5 text-xs text-neutral-600">
                    Open an entity page to add its current context.
                  </p>
                )}
              </div>
            )}
          </div>

          <div className="flex items-center">
            <AuiIf condition={(s) => s.thread.isRunning}>
              <ComposerPrimitive.Cancel className="flex size-8 items-center justify-center rounded-full bg-neutral-300 disabled:opacity-30">
                <Square className="size-4 text-neutral-800" />
              </ComposerPrimitive.Cancel>
            </AuiIf>
            <AuiIf condition={(s) => !s.thread.isRunning}>
              <ComposerPrimitive.Send className="flex size-8 items-center justify-center rounded-full bg-neutral-300 disabled:opacity-30">
                <ArrowUpIcon className="size-4 text-neutral-800" />
              </ComposerPrimitive.Send>
            </AuiIf>
          </div>
        </div>
      </ComposerPrimitive.Root>
    </div>
  );
}
